import { Routes } from '@angular/router';
import { authGuard } from './shared/guards/auth.guard';
import { guestGuard } from '@/shared/guards/guest-guard';
import { twoFactorGuard } from '@/shared/guards/two-factor.guard';
import { roleGuard } from './shared/guards/role.guard';

// `breadcrumb` y `breadcrumbTrail.label` guardan claves de los diccionarios de
// `assets/i18n`, no el texto: las resuelve app.breadcrumb.ts al pintarlas.

export const routes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: '/home'
    },
    {
        path: 'auth',
        canActivate: [guestGuard],
        loadChildren: () => import('./pages/auth/auth.routes').then((m) => m.authRoutes)
    },
    {
        path: 'rate-your-driver/:id',
        loadComponent: () => import('@/pages/public/driver-rate/driver-rate').then((m) => m.DriverRate)
    },
    {
        path: '',
        loadComponent: () => import('./layout/components/authenticated/authenticated.component').then((m) => m.AuthenticatedLayoutComponent),
        canActivate: [authGuard, twoFactorGuard],
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'home'
            },
            {
                path: 'home',
                loadComponent: () => import('./shared/components/role-redirect/role-redirect.component').then((m) => m.RoleRedirectComponent),
                data: { breadcrumb: 'navigation.breadcrumb.home' }
            },
            {
                path: 'dashboard',
                loadComponent: () => import('./pages/dashboard/dashboard.component').then((m) => m.DashboardComponent),
                canActivate: [roleGuard],
                data: { breadcrumb: 'navigation.breadcrumb.dashboard', roles: ['superadmin', 'manager'] }
            },
            {
                path: 'dispatcher-bookings',
                loadComponent: () => import('./pages/dispatcher/dispatcher-bookings/dispatcher-bookings.component').then((m) => m.DispatcherBookingsComponent),
                canActivate: [roleGuard],
                data: { breadcrumbTrail: [{ label: 'navigation.breadcrumb.bookings' }], breadcrumb: 'navigation.breadcrumb.dispatch_board', roles: ['superadmin', 'manager', 'csr', 'dispatcher'] }
            },
            {
                path: 'bookings/new',
                loadComponent: () => import('./pages/bookings/booking-form/booking-form.component').then((m) => m.BookingFormComponent),
                canActivate: [roleGuard],
                data: { breadcrumbTrail: [{ label: 'navigation.breadcrumb.bookings' }], breadcrumb: 'navigation.breadcrumb.create_booking', roles: ['superadmin', 'manager', 'csr', 'dispatcher'] }
            },
            {
                path: 'bookings/new-with-client/:clientID',
                loadComponent: () => import('./pages/bookings/booking-form/booking-form.component').then((m) => m.BookingFormComponent),
                canActivate: [roleGuard],
                data: { breadcrumbTrail: [{ label: 'navigation.breadcrumb.bookings' }], breadcrumb: 'navigation.breadcrumb.create_booking', roles: ['superadmin', 'manager', 'csr', 'dispatcher', 'client'] }
            },
            {
                path: 'bookings/:id/edit',
                loadComponent: () => import('./pages/bookings/booking-form/booking-form.component').then((m) => m.BookingFormComponent),
                canActivate: [roleGuard],
                data: { breadcrumbTrail: [{ label: 'navigation.breadcrumb.bookings' }], breadcrumb: 'navigation.breadcrumb.edit_booking', roles: ['superadmin', 'manager', 'csr', 'driver', 'dispatcher', 'client'] }
            },
            {
                path: 'profile',
                loadComponent: () => import('./pages/profile/profile.component').then((m) => m.ProfileComponent),
                data: { breadcrumb: 'navigation.breadcrumb.profile', roles: ['superadmin', 'manager', 'csr', 'dispatcher', 'driver', 'client'] }
            },
            {
                path: 'clients',
                canActivate: [roleGuard],
                loadComponent: () => import('@/pages/users/clients/client-list/client-list').then((m) => m.ClientList),
                data: { breadcrumbTrail: [{ label: 'navigation.breadcrumb.users' }], breadcrumb: 'navigation.breadcrumb.clients', roles: ['superadmin', 'manager', 'csr', 'dispatcher'] }
            },
            {
                path: 'clients/:id',
                canActivate: [roleGuard],
                loadComponent: () => import('@/pages/users/clients/client-show/client-show').then((m) => m.ClientShow),
                data: {
                    breadcrumbTrail: [{ label: 'navigation.breadcrumb.users' }, { label: 'navigation.breadcrumb.clients', url: '/clients', roles: ['superadmin', 'manager', 'csr', 'dispatcher'] }],
                    breadcrumb: 'navigation.breadcrumb.client',
                    roles: ['superadmin', 'manager', 'csr', 'client', 'dispatcher']
                }
            },
            {
                path: 'drivers',
                canActivate: [roleGuard],
                loadComponent: () => import('@/pages/users/drivers/driver-list/driver-list').then((m) => m.DriverList),
                data: { breadcrumbTrail: [{ label: 'navigation.breadcrumb.users' }, { label: 'navigation.breadcrumb.drivers' }], breadcrumb: 'navigation.breadcrumb.drivers_list', roles: ['superadmin', 'manager', 'csr', 'dispatcher'] }
            },
            {
                path: 'drivers/create',
                canActivate: [roleGuard],
                loadComponent: () => import('@/pages/users/drivers/driver-create/driver-create').then((m) => m.DriverCreate),
                data: {
                    breadcrumbTrail: [{ label: 'navigation.breadcrumb.users' }, { label: 'navigation.breadcrumb.drivers', url: '/drivers', roles: ['superadmin', 'manager', 'csr', 'dispatcher'] }],
                    breadcrumb: 'navigation.breadcrumb.create_driver',
                    roles: ['superadmin', 'manager']
                }
            },
            {
                path: 'drivers/recruiting',
                canActivate: [roleGuard],
                loadComponent: () => import('@/pages/users/drivers/recruiting-list/recruiting-list').then((m) => m.RecruitingList),
                data: {
                    breadcrumbTrail: [{ label: 'navigation.breadcrumb.users' }, { label: 'navigation.breadcrumb.drivers', url: '/drivers', roles: ['superadmin', 'manager', 'csr', 'dispatcher'] }],
                    breadcrumb: 'navigation.breadcrumb.recruiting',
                    roles: ['superadmin', 'manager', 'csr', 'dispatcher']
                }
            },
            {
                path: 'drivers/recruiting/:id',
                canActivate: [roleGuard],
                loadComponent: () => import('@/pages/users/drivers/recruiting-detail/recruiting-detail').then((m) => m.RecruitingDetail),
                data: {
                    breadcrumbTrail: [
                        { label: 'navigation.breadcrumb.users' },
                        { label: 'navigation.breadcrumb.drivers', url: '/drivers', roles: ['superadmin', 'manager', 'csr', 'dispatcher'] },
                        { label: 'navigation.breadcrumb.recruiting', url: '/drivers/recruiting', roles: ['superadmin', 'manager', 'csr', 'dispatcher'] }
                    ],
                    breadcrumb: 'navigation.breadcrumb.applicant',
                    roles: ['superadmin', 'manager', 'csr', 'dispatcher']
                }
            },
            {
                path: 'drivers/:id',
                canActivate: [roleGuard],
                loadComponent: () => import('@/pages/users/drivers/driver-show/driver-show').then((m) => m.DriverShow),
                data: {
                    breadcrumbTrail: [{ label: 'navigation.breadcrumb.users' }, { label: 'navigation.breadcrumb.drivers', url: '/drivers', roles: ['superadmin', 'manager', 'csr', 'dispatcher'] }],
                    breadcrumb: 'navigation.breadcrumb.driver_info',
                    roles: ['superadmin', 'manager', 'csr', 'driver', 'dispatcher']
                }
            },
            {
                path: 'staff-members',
                canActivate: [roleGuard],
                loadComponent: () => import('@/pages/users/staff/staff-list/staff-list').then((m) => m.StaffList),
                data: { breadcrumbTrail: [{ label: 'navigation.breadcrumb.users' }], breadcrumb: 'navigation.breadcrumb.staff_members', roles: ['superadmin', 'manager'] }
            },
            {
                path: 'staff-members/create',
                canActivate: [roleGuard],
                loadComponent: () => import('@/pages/users/staff/staff-create/staff-create').then((m) => m.StaffCreate),
                data: {
                    breadcrumbTrail: [{ label: 'navigation.breadcrumb.users' }, { label: 'navigation.breadcrumb.staff_members', url: '/staff-members', roles: ['superadmin', 'manager'] }],
                    breadcrumb: 'navigation.breadcrumb.create_staff_member',
                    roles: ['superadmin', 'manager']
                }
            },
            {
                path: 'staff-members/:id',
                canActivate: [roleGuard],
                loadComponent: () => import('@/pages/users/staff/staff-show/staff-show').then((m) => m.StaffShow),
                data: {
                    breadcrumbTrail: [{ label: 'navigation.breadcrumb.users' }, { label: 'navigation.breadcrumb.staff_members', url: '/staff-members', roles: ['superadmin', 'manager'] }],
                    breadcrumb: 'navigation.breadcrumb.staff_member_info',
                    roles: ['superadmin', 'manager']
                }
            },
            {
                path: 'configuration',
                canActivate: [roleGuard],
                loadComponent: () => import('@/pages/admin/configuration-list/configuration-list').then((m) => m.ConfigurationList),
                data: { breadcrumb: 'navigation.breadcrumb.configuration', roles: ['superadmin'] }
            },
            {
                path: 'billing',
                canActivate: [roleGuard],
                loadComponent: () => import('@/pages/billing/billing/billing').then((m) => m.Billing),
                data: { breadcrumb: 'navigation.breadcrumb.billing', roles: ['superadmin', 'manager'] }
            },
            {
                path: 'forbidden',
                loadComponent: () => import('./pages/auth/access/access.component').then((m) => m.AccessComponent),
                data: { breadcrumb: 'navigation.breadcrumb.access_denied' }
            }
        ]
    }
];
